/**
 * Organizaciones, usuarios y terminales.
 *
 * v1 opera con una sola organización (una casa/local), pero todo el modelo ya está
 * multi-tenant por `org_id` para no tener que migrar después (§20.5).
 */
import { z } from 'zod';
import { RoleSchema } from '../roles.js';
import { CurrencySchema } from '../money.js';

export const OrganizationSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(2).max(120),
  slug: z.string().regex(/^[a-z0-9-]{2,40}$/),
  defaultCurrency: CurrencySchema,
  createdAt: z.string().datetime({ offset: true }),
});
export type Organization = z.infer<typeof OrganizationSchema>;

export const UserStatusSchema = z.enum(['active', 'suspended']);
export type UserStatus = z.infer<typeof UserStatusSchema>;

export const UserSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  email: z.string().email(),
  displayName: z.string().min(1).max(120),
  /** Nombre de ciudadano vinculado: permite que un bettor vea boletos de ventanilla. */
  citizenName: z.string().max(80).nullable(),
  role: RoleSchema,
  status: UserStatusSchema,
  createdAt: z.string().datetime({ offset: true }),
  lastLoginAt: z.string().datetime({ offset: true }).nullable(),
});
export type User = z.infer<typeof UserSchema>;

export const TerminalKindSchema = z.enum(['ventanilla', 'caja', 'hud', 'movil']);
export type TerminalKind = z.infer<typeof TerminalKindSchema>;

export const TerminalSchema = z.object({
  id: z.string().min(1).max(64),
  orgId: z.string().min(1),
  label: z.string().min(1).max(60),
  kind: TerminalKindSchema,
  lastSeenAt: z.string().datetime({ offset: true }).nullable(),
});
export type Terminal = z.infer<typeof TerminalSchema>;

/** Un terminal está "presente" si latió en los últimos 30 s. */
export const TERMINAL_PRESENCE_MS = 30_000;
